import { alarmToggleActions, enableField, disableField } from '../../actions';
import { Section, SelectorIdentifier } from '../../models';
import { TritonSectionIds, Mp2ConfigureAlarmsFieldId } from './base-config-models';
import { DEFAULT_FLOW_LOSS_PERCENTAGE } from 'app/shared/constant';
import { field } from '../../core/field-schema';
import { required, pattern, min, max } from '../../core/validator-builders';
import { SamplerTypeOption } from '../../../models/location-card.model';
import { AlarmDevice, FlowLossAlarmDevice, TritonLocationCardState, TritonMonitorConfiguration } from './models';
import { NonSensorDeviceNames } from 'app/shared/enums/device-type-code';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { getValidators } from './alarms';
import { roundToDecimal } from '../../models/utils';

/**
 * Triton MP2 Alarms Configuration Section
 */
export const mp2configureAlarms = (state: TritonLocationCardState): Section => {
    const config = state.monitorConfiguration as TritonMonitorConfiguration;
    const settings = state.settings;

    const alarmData = config && config.devices ? (config.devices.find((v) => v.name === NonSensorDeviceNames.Alarm2) as AlarmDevice) : null;
    const flowLossData = config && config.devices ? (config.devices.find((v) => v.name === NonSensorDeviceNames.FlowLoss2) as FlowLossAlarmDevice) : null;

    const depthAlarmValidators = [required(), ...getValidators(settings.isMetric)];
    const highFlowValidators = [
        required(),
        min({ value: 0 }),
        max({ value: 100000 }),
        pattern(
            settings.isMetric
                ? {
                      value: REGEX_CONFIG.numeric0_100000With1DecimalPlacesPattern,
                      message: 'COMMON.NUMERIC_PRECISION_ERROR_METRIC',
                  }
                : {
                      value: REGEX_CONFIG.numeric0_100000With3DecimalPlacesPattern,
                      message: 'COMMON.NUMERIC_PRECISION_ERROR_3_DECIMALS',
                  },
        ),
    ];

    return {
        info: {
            id: TritonSectionIds.Mp2Alarms,
            title: 'COMMON.MP_CONFIGURE_ALARMS',
            displayTitle: true,
            expandable: true,
            expanded: false,
        },
        rows: [
            // Low Level Alarm Row
            {
                info: {
                    id: 'low-level-alarms-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(Mp2ConfigureAlarmsFieldId.LowLevelEnable, 'lowLevelEnable', {
                        placeholder: 'COMMON.LOW_LEVEL_ALARM',
                        value: alarmData ? alarmData.lowLevelEnable : false,
                        panelClass: 'alarm-slide-toggle',
                        events: {
                            onChange: (value) => alarmToggleActions(value, Mp2ConfigureAlarmsFieldId.LowLevelThreshold, depthAlarmValidators),
                        },
                    }),
                    field.number(Mp2ConfigureAlarmsFieldId.LowLevelThreshold, 'lowLevelThreshold', {
                        placeholder: 'COMMON.DEPTH_THRESHOLD_OF_LOW_LEVEL',
                        placeholderSuffix: settings.depthUnit,
                        panelClass: 'alarm-input',
                        value: alarmData ? roundToDecimal(alarmData.lowLevelThreshold, 1) : 0,
                        decimalPlaces: settings.isMetric ? 1 : 2,
                        disabled: alarmData ? !alarmData.lowLevelEnable : true,
                        validators: depthAlarmValidators,
                    }),
                ],
            },
            // Full Pipe Alarm Row
            {
                info: {
                    id: 'full-pipe-alarms-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(Mp2ConfigureAlarmsFieldId.FullPipeEnable, 'fullPipeEnable', {
                        placeholder: 'COMMON.FULL_PIPE_ALARM',
                        panelClass: 'alarm-slide-toggle',
                        value: alarmData ? alarmData.fullPipeEnable : false,
                        events: {
                            onChange: (value) => [
                                value
                                    ? enableField({ fieldId: Mp2ConfigureAlarmsFieldId.FullPipeThreshold })
                                    : disableField({ fieldId: Mp2ConfigureAlarmsFieldId.FullPipeThreshold }),
                            ],
                        },
                    }),
                    field.number(Mp2ConfigureAlarmsFieldId.FullPipeThreshold, 'fullPipethreshold', {
                        readonly: true,
                        placeholder: 'COMMON.DEPTH_THRESHOLD_OF_FULL_PIPE',
                        placeholderSuffix: settings.depthUnit,
                        panelClass: 'alarm-input',
                        value: alarmData ? roundToDecimal(alarmData.fullPipeThreshold, settings.isMetric ? 1 : 2) : 0,
                        decimalPlaces: settings.isMetric ? 1 : 2,
                        disabled: alarmData ? !alarmData.fullPipeEnable : true,
                        validators: getValidators(settings.isMetric),
                    }),
                ],
            },
            // High Level Alarm Row
            {
                info: {
                    id: 'high-level-alarms-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(Mp2ConfigureAlarmsFieldId.HighLevelEnable, 'highLevelEnable', {
                        placeholder: 'COMMON.HIGH_LEVEL_ALARM',
                        panelClass: 'alarm-slide-toggle',
                        value: alarmData ? alarmData.highLevelEnable : false,
                        events: {
                            onChange: (value) => alarmToggleActions(value, Mp2ConfigureAlarmsFieldId.HighLevelThreshold, depthAlarmValidators),
                        },
                    }),
                    field.number(Mp2ConfigureAlarmsFieldId.HighLevelThreshold, 'highLevelThreshold', {
                        placeholder: 'COMMON.DEPTH_THRESHOLD_OF_HIGH_LEVEL',
                        placeholderSuffix: settings.depthUnit,
                        panelClass: 'alarm-input',
                        value: alarmData ? roundToDecimal(alarmData.highLevelThreshold, 1) : 0,
                        decimalPlaces: settings.isMetric ? 1 : 2,
                        disabled: alarmData ? !alarmData.highLevelEnable : true,
                        validators: depthAlarmValidators,
                    }),
                ],
            },
            // High-High Level Alarm Row
            {
                info: {
                    id: 'high-high-level-alarms-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(Mp2ConfigureAlarmsFieldId.HighHighEnable, 'highHighEnable', {
                        placeholder: 'COMMON.HIGH_HIGH_ALARAM',
                        panelClass: 'alarm-slide-toggle',
                        value: alarmData ? alarmData.highHighEnable : false,
                        events: {
                            onChange: (value) => alarmToggleActions(value, Mp2ConfigureAlarmsFieldId.HighHighThreshold, depthAlarmValidators),
                        },
                    }),
                    field.number(Mp2ConfigureAlarmsFieldId.HighHighThreshold, 'highHighThreshold', {
                        placeholder: 'COMMON.DEPTH_THRESHOLD_OF_HIGH_HIGH',
                        placeholderSuffix: settings.depthUnit,
                        panelClass: 'alarm-input',
                        value: alarmData ? roundToDecimal(alarmData.highHighThreshold, settings.isMetric ? 1 : 2) : 0,
                        decimalPlaces: settings.isMetric ? 1 : 2,
                        disabled: alarmData ? !alarmData.highHighEnable : true,
                        validators: depthAlarmValidators,
                    }),
                ],
            },

            // High Flow Alarm Row
            {
                info: {
                    id: 'high-flow-alarm-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(Mp2ConfigureAlarmsFieldId.HighFlowEnable, 'highFlowEnable', {
                        placeholder: 'COMMON.HIGH_FLOW_ALARM',
                        panelClass: 'alarm-slide-toggle',
                        value: alarmData ? alarmData.highFlowEnable : false,
                        events: {
                            onChange: (value) => alarmToggleActions(value, Mp2ConfigureAlarmsFieldId.HighFlowThreshold, highFlowValidators),
                        },
                    }),
                    field.number(Mp2ConfigureAlarmsFieldId.HighFlowThreshold, 'highFlowThreshold', {
                        placeholder: 'COMMON.QUANTITY_THRESHOLD_OF',
                        placeholderSuffix: settings.flowUnit,
                        panelClass: 'alarm-input',
                        value: alarmData?.highFlowThreshold ?? 0,
                        disabled: alarmData ? !alarmData.highFlowEnable : true,
                        validators: highFlowValidators,
                    }),
                ],
            },

            // Overflow Alarm Row - no inputs to enable/disable
            {
                info: {
                    id: 'overflow-alarm-row',
                    panelClass: 'each-configure-alarm margin-t-b-15',
                },
                items: [
                    field.toggle(Mp2ConfigureAlarmsFieldId.OverflowEnable, 'overflowEnable', {
                        placeholder: 'COMMON.OVERFLOW_ALARMS',
                        panelClass: 'alarm-slide-toggle',
                        value: alarmData ? alarmData.overflowEnable : false,
                    }),
                ],
            },
            // Flow Loss Alarm Row
            {
                info: {
                    id: 'flow-loss-alarm-row',
                    panelClass: 'each-configure-alarm flow-loss-alaram',
                },
                items: [
                    field.toggle(Mp2ConfigureAlarmsFieldId.FlowLossAlarm, 'flowLossAlarm', {
                        placeholder: 'COMMON.FLOW_LOSS_ALARAM',
                        panelClass: 'alarm-slide-toggle',
                        value: flowLossData ? true : false,
                        events: {
                            onChange: (value) => [
                                // Enable/disable all fields in this row
                                value
                                    ? enableField({ fieldId: Mp2ConfigureAlarmsFieldId.FlowLossType })
                                    : disableField({ fieldId: Mp2ConfigureAlarmsFieldId.FlowLossType }),
                                value
                                    ? enableField({ fieldId: Mp2ConfigureAlarmsFieldId.Percent })
                                    : disableField({ fieldId: Mp2ConfigureAlarmsFieldId.Percent }),
                                value
                                    ? enableField({ fieldId: Mp2ConfigureAlarmsFieldId.Sensitivity })
                                    : disableField({ fieldId: Mp2ConfigureAlarmsFieldId.Sensitivity }),
                                value
                                    ? enableField({ fieldId: Mp2ConfigureAlarmsFieldId.ClearPatternHistory })
                                    : disableField({ fieldId: Mp2ConfigureAlarmsFieldId.ClearPatternHistory }),
                            ],
                        },
                    }),
                    field.select<SamplerTypeOption, number>(Mp2ConfigureAlarmsFieldId.FlowLossType, 'flowLossType', {
                        label: 'COMMON.TYPE',
                        valueMapper: (option) => option.id,
                        displayMapper: (option) => option.name,
                        panelClass: 'alarm-input-70',
                        value: flowLossData ? flowLossData.flowLossType : 0,
                        disabled: flowLossData ? false : true,
                        optionsSelector: SelectorIdentifier.FlowLossAlarmTypes,
                    }),
                    field.select<SamplerTypeOption, number>(Mp2ConfigureAlarmsFieldId.Percent, 'percent', {
                        panelClass: 'alarm-input-70',
                        valueMapper: (option) => option.id,
                        displayMapper: (option) => option.name,
                        label: 'COMMON.PERCENTAGE',
                        optionsSelector: SelectorIdentifier.FlowLossPercentange,
                        disabled: flowLossData ? false : true,
                        value: flowLossData ? flowLossData.percent : DEFAULT_FLOW_LOSS_PERCENTAGE,
                    }),
                    field.select<SamplerTypeOption, number>(Mp2ConfigureAlarmsFieldId.Sensitivity, 'sensitivity', {
                        panelClass: 'alarm-input-70',
                        label: 'COMMON.SENSITIVITY',
                        valueMapper: (option) => option.id,
                        displayMapper: (option) => option.name,
                        value: flowLossData ? flowLossData.sensitivity : 1,
                        disabled: flowLossData ? false : true,
                        optionsSelector: SelectorIdentifier.FlowLossSensitivity,
                    }),
                    field.toggle(Mp2ConfigureAlarmsFieldId.ClearPatternHistory, 'clearPatternHistory', {
                        panelClass: 'alarm-slide-140',
                        placeholder: 'COMMON.RESET_PATTERN_HISTORY_ON_ACTIVATION',
                        value: flowLossData ? flowLossData.clearPatternHistory : false,
                        disabled: flowLossData ? false : true,
                    }),
                ],
            },
        ],
    };
};
